/* eslint-disable jsdoc/require-param-type, jsdoc/require-returns-type */
import { get } from 'svelte/store';
import { app, setGlobalStore } from '$lib/stores/app/store';
import { user } from '$lib/stores/user/store';
import { ApiService } from '$lib/services/api';
import { getDeviceId } from '$lib/utils/device-id';
import {
	ABC_ATTRIBUTES,
	Sentry,
	instrument,
	setExperimentRouteAttributes,
} from '$lib/services/sentry';
import { page } from '$app/stores';
import {
	has1_0Account,
	has2_0Account,
	is1_0OriginatingUser,
	is2_0OriginatingUser,
} from '$lib/utils/aofl-accounts';
import {
	AB_API_HOSTS,
	DATA_KEYS,
	DATA_TYPES,
	DEVICE_KEY_MAP,
	PROFILE,
} from '$lib/services/experiments/client/constants';
import {
	ExperimentClient,
	type DataItem,
	type ExperimentApi,
	type ExperimentEntry,
	type ExperimentRequestContext,
	type ExperimentStatePort,
	type ExperimentTelemetry,
	type GetExperimentsResponse,
	type UserData,
} from '@mkt-client/experiment-client';

/**
 * Page-level context nested inside event_metadata (client context fields + page fields).
 * This is the classic-specific shape of the experiment API's protobuf `Any` slot.
 */
interface PageMetadata {
	// client context (from ApiService.getClientContext)
	app_version?: string;
	is_web?: boolean;
	is_automated_testing?: boolean;
	preserve_session_device_id?: boolean;
	client_url?: string | null;
	viewer_country?: string;
	viewer_country_region?: string;
	client_platform?: string;
	// page fields
	page_name: string;
	page_url: string;
	navigator?: string;
	window_url?: string;
	svelte_page_url?: unknown;
	is_tablet?: string | null;
	is_desktop?: string | null;
}

/** Classic event_metadata payload (proto field 6, google.protobuf.Any). */
interface EventMetadata {
	page_metadata: PageMetadata;
	subscription_metadata: { user_subscription_type: string | null };
}

/** App-store experiment state wired into the package's state port. */
const state: ExperimentStatePort = {
	getExperiments() {
		return (get(app)?.global?.experiments ?? []) as ExperimentEntry[];
	},
	async setExperiments(experiments) {
		await setGlobalStore({ experiments });
	},
};

/** ApiService bound to the package's experiment API port. */
const api: ExperimentApi<EventMetadata> = {
	getExperiments(host, payload) {
		return ApiService.getExperiments(host, payload) as Promise<GetExperimentsResponse>;
	},
};

/** Sentry bound to the package's telemetry port. */
const telemetry: ExperimentTelemetry = {
	captureException(error, context) {
		Sentry.captureException(error, context as Parameters<typeof Sentry.captureException>[1]);
	},
	captureMessage(message, context) {
		Sentry.captureMessage(message, context as Parameters<typeof Sentry.captureMessage>[1]);
	},
};

const client = new ExperimentClient<EventMetadata>({ api, state, telemetry });

/**
 * Returns the experiment/analytics API host for the current environment.
 * @returns API host URL for the current environment.
 */
function getApiHost(): string {
	const {
		meta: { environment },
	} = get(app);
	return AB_API_HOSTS[environment as keyof typeof AB_API_HOSTS];
}

/** Per-call overrides for the experiment request context. */
interface ExperimentCallOptions {
	/**
	 * Session id for this Get. Defaults to 'null'. The Signifyd pre-auth experiment
	 * passes the Signifyd payment-session UUID so the assignment is keyed on the same
	 * id used for the device fingerprint and the transaction. Scoped per-call so other
	 * experiments keep sending 'null'.
	 */
	sessionId?: string;
}

/**
 * Builds the user payload for experiment API calls (device_id, master_id, session_id, user_id).
 * @param options - Per-call overrides (e.g. sessionId).
 * @returns User payload for the experiment API.
 */
function getUserData(options: ExperimentCallOptions = {}): UserData {
	const userStore = get(user);
	return {
		device_id: getDeviceId() ?? '',
		master_id: userStore.userToProductUuid || 'null',
		session_id: options.sessionId || 'null',
		user_id: userStore.userToProductUuid || 'null',
	};
}

/**
 * Builds the device/data array for experiment API calls from app meta and global state.
 * @returns Device/data items for the experiment API.
 */
function getDeviceData(): DataItem[] {
	const appMeta = get(app).meta;
	const appGlobal = get(app).global as Record<string, unknown>;
	const deviceId = getDeviceId() ?? '';

	// Use a Map keyed by payload key to deduplicate entries that may
	// exist in both app.meta (stale localStorage) and app.global
	// after keys were moved between store sections. Global wins.
	const deviceDataMap = new Map<string, DataItem>();

	for (const [key, storeKey] of Object.entries(DEVICE_KEY_MAP)) {
		if (appMeta[storeKey as keyof typeof appMeta] === undefined) continue;
		deviceDataMap.set(key, {
			type: DATA_TYPES.DEVICE,
			id: deviceId,
			key,
			value: String(appMeta[storeKey as keyof typeof appMeta]),
		});
	}

	for (const [key, storeKey] of Object.entries(DEVICE_KEY_MAP)) {
		if (appGlobal[storeKey] === undefined) continue;
		deviceDataMap.set(key, {
			type: DATA_TYPES.DEVICE,
			id: deviceId,
			key,
			value: String(appGlobal[storeKey]),
		});
	}

	return [...deviceDataMap.values()];
}

/**
 * Builds subscription-related data items for the experiment API when the user has a userToProductUuid.
 * @returns Subscription data items, or null when user has no userToProductUuid.
 */
function getSubscriptionData(): DataItem[] | null {
	const userStore = get(user);
	if (!userStore.userToProductUuid) return null;
	const subscriptionStatus: DataItem = {
		type: DATA_TYPES.MASTER,
		id: userStore.userToProductUuid ?? '',
		key: DATA_KEYS.SUBSCRIPTION_STATUS,
		value: userStore.accountStatus ?? '',
	};
	let value: string = '';
	switch (true) {
		case has1_0Account() && !has2_0Account():
			value = '1.0_only';
			break;
		case is1_0OriginatingUser() && has2_0Account():
			value = '1.0_originating_mixed';
			break;
		case has2_0Account() && !has1_0Account():
			value = '2.0_only';
			break;
		case has2_0Account() && is2_0OriginatingUser():
			value = '2.0_originating_mixed';
			break;
		default:
			value = '';
			break;
	}
	const userSubscriptionType: DataItem = {
		type: DATA_TYPES.MASTER,
		id: userStore.userToProductUuid ?? '',
		key: DATA_KEYS.USER_SUBSCRIPTION_TYPE,
		value,
	};
	const reactivatedUser: DataItem = {
		type: DATA_TYPES.MASTER,
		id: userStore.userToProductUuid ?? '',
		key: DATA_KEYS.REACTIVATED_USER,
		value: userStore.isReactivated ? 'true' : 'false',
	};
	const isFromSpecialOfferPage = userStore.userMetaData?.special_offer;
	const specialOfferUser: DataItem = {
		type: DATA_TYPES.MASTER,
		id: userStore.userToProductUuid ?? '',
		key: DATA_KEYS.SPECIAL_OFFER_USER,
		value: isFromSpecialOfferPage ? 'true' : 'false',
	};
	return [subscriptionStatus, userSubscriptionType, reactivatedUser, specialOfferUser];
}

/**
 * Builds the device + subscription data items sent in the Get payload.
 * @returns Combined device/subscription data items.
 */
function getData(): DataItem[] {
	const subscriptionData = getSubscriptionData();
	const data = [...getDeviceData()];
	if (subscriptionData) data.push(...subscriptionData);
	return data;
}

/**
 * Builds the subscription metadata object sent inside event_metadata.
 * @returns Object with user_subscription_type, or { user_subscription_type: null } when unavailable.
 */
function getSubscriptionMetadata(): { user_subscription_type: string | null } {
	const subscriptionData = getSubscriptionData();
	const userSubscription =
		subscriptionData?.find((item) => item.key === DATA_KEYS.USER_SUBSCRIPTION_TYPE)?.value ?? null;
	return { user_subscription_type: userSubscription };
}

/**
 * Builds the page_metadata object nested inside event_metadata for Get API calls.
 * @returns Page metadata object for the API.
 */
function buildPageMetadata(): PageMetadata {
	const meta = get(app).meta;
	return {
		...ApiService.getClientContext(),
		page_name: get(page)?.url?.pathname ?? '',
		page_url: get(page)?.url?.href ?? get(page)?.url?.pathname ?? '',
		navigator: window.navigator?.userAgent ?? undefined,
		is_tablet: meta?.isTabletViewer ?? null,
		is_desktop: meta?.isDesktopViewer ?? null,
	};
}

/**
 * Builds the per-call request context from classic stores and environment.
 * @param options - Per-call overrides (e.g. sessionId).
 * @returns Request context consumed by the package client.
 */
function buildContext(
	options: ExperimentCallOptions = {}
): ExperimentRequestContext<EventMetadata> {
	const eventMetadata: EventMetadata = {
		page_metadata: buildPageMetadata(),
		subscription_metadata: getSubscriptionMetadata(),
	};
	return {
		host: getApiHost(),
		profile: PROFILE,
		user: getUserData(options),
		data: getData(),
		eventMetadata,
	};
}

/**
 * Classic SvelteKit adapter over `@mkt-client/experiment-client`. Builds the
 * per-call request context from classic stores and delegates to the package.
 */
export const experimentClient = {
	/**
	 * Returns the assigned variant at a divergence point and records exposure (Get with auto_mark_seen).
	 * @param experimentKey - Experiment key (e.g. from ACTIVE_EXPERIMENTS).
	 * @param autoMarkSeen - Whether to mark the experiment as seen.
	 * @param options - Per-call request-context overrides (e.g. sessionId).
	 * @returns The assigned variant string, or null if not assigned or the API failed.
	 */
	initializeExperiment(
		experimentKey: string,
		autoMarkSeen = true,
		options: ExperimentCallOptions = {}
	): Promise<string | null> {
		return instrument(
			'experiment.init',
			`Experiment init ${experimentKey}`,
			async () => {
				const result = await client.initializeExperiment(
					experimentKey,
					buildContext(options),
					autoMarkSeen
				);
				setExperimentRouteAttributes(get(app).global.experiments);
				return result;
			},
			{ [ABC_ATTRIBUTES.EXPERIMENT_KEY]: experimentKey }
		);
	},

	/**
	 * @param experimentKey - Experiment key to look up.
	 * @param variant - Variant string to compare (e.g. 'test', 'default').
	 * @returns true if the stored variant matches, false otherwise or if missing.
	 */
	isVariantActive(experimentKey: string, variant: string): boolean {
		return client.isVariantActive(experimentKey, variant);
	},

	/**
	 * @param experimentKey - Experiment key to look up.
	 * @param variants - List of variant strings (e.g. ['test', 'test-a']).
	 * @returns true if the stored variant is in the list, false otherwise or if missing.
	 */
	isAnyVariantActive(experimentKey: string, variants: string[]): boolean {
		return client.isAnyVariantActive(experimentKey, variants);
	},

	/**
	 * @param experimentKeys - List of experiment keys to check.
	 * @returns true if any key is assigned, false if none or store empty.
	 */
	isAnyAssigned(experimentKeys: string[]): boolean {
		return client.isAnyAssigned(experimentKeys);
	},

	/**
	 * Marks an already-assigned experiment as seen without re-fetching assignment.
	 * No-ops when the experiment is missing, unassigned, or already marked seen.
	 * @param experimentKey - Experiment key (e.g. from ACTIVE_EXPERIMENTS).
	 * @returns Resolves when the seen event has been recorded or skipped.
	 */
	async markExperimentAsSeen(experimentKey: string): Promise<void> {
		if (!experimentKey) return;

		const experiments = (get(app).global.experiments ?? []) as ExperimentEntry[];
		const experiment = experiments.find((exp) => exp.key === experimentKey);
		if (!experiment?.experiment_id || experiment.seen) return;

		const payload = {
			...experiment,
			profile: PROFILE,
			user: getUserData(),
			pageMetadata: buildPageMetadata(),
			subscriptionMetadata: getSubscriptionMetadata(),
		};

		try {
			await ApiService.markExperimentSeen(getApiHost(), payload);
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Failed To Mark Experiment Seen',
					payload,
				},
			});
		}

		experiment.seen = true;
		await setGlobalStore({ experiments });
	},
};
